import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Json } from "@how/database";
import { publicCampusSchema, type PublicCampus } from "@how/website-core";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/** One service on a campus, in the shape the public site's service_times block draws. */
export type ServiceTime = PublicCampus["serviceTimes"][number];

export interface CampusRow {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly addressLines: readonly string[];
  readonly phone: string | null;
  readonly mapUrl: string | null;
  /** Null means the church's own zone. */
  readonly timeZone: string | null;
  readonly serviceTimes: readonly ServiceTime[];
  readonly isActive: boolean;
  readonly sortOrder: number;
  /** The campus page's text on the website (Milestone 5 B1): plain paragraphs. */
  readonly description: string | null;
  /** In the website's location chooser, or reachable by its link only. */
  readonly isListed: boolean;
  /** People whose home campus this is. */
  readonly peopleCount: number;
}

/**
 * The church's campuses, active and archived, in the church's order. A church has a handful, so the list is read
 * whole. Service times are stored as JSON and parsed through the same contract the public site uses, so a row the
 * site would refuse shows here as having none rather than breaking the screen.
 */
export function useCampuses() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["campuses", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<CampusRow[]> => {
      const { data, error } = await supabase
        .from("campuses")
        .select(
          "id, name, slug, address_lines, phone, map_url, time_zone, service_times, is_active, sort_order, description, is_listed, people(count)",
        )
        // RLS admits every church the reader belongs to; the screen shows one church.
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((row) => {
        const times = publicCampusSchema.shape.serviceTimes.safeParse(row.service_times);
        return {
          id: row.id,
          name: row.name,
          slug: row.slug,
          addressLines: row.address_lines,
          phone: row.phone,
          mapUrl: row.map_url,
          timeZone: row.time_zone,
          serviceTimes: times.success ? times.data : [],
          isActive: row.is_active,
          sortOrder: row.sort_order,
          description: row.description,
          isListed: row.is_listed,
          peopleCount: row.people[0]?.count ?? 0,
        };
      });
    },
  });
}

function useCampusInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["campuses", tenantId] });
    // A campus's name shows on every person who calls it home.
    await client.invalidateQueries({ queryKey: ["person", tenantId] });
    await client.invalidateQueries({ queryKey: ["people", tenantId] });
  };
}

export interface CampusFields {
  readonly name: string;
  /** Empty: derived from the name by the database. */
  readonly slug: string;
  /** One line per address line, as typed. */
  readonly address: string;
  readonly phone: string;
  readonly mapUrl: string;
  /** Empty: the church's own zone. */
  readonly timeZone: string;
  readonly serviceTimes: readonly ServiceTime[];
  readonly sortOrder: number;
  /** What a visitor reads on the campus's page; blank lines make paragraphs. */
  readonly description: string;
  readonly isListed: boolean;
}

export function useSaveCampus() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useCampusInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly campusId: string | null;
      readonly fields: CampusFields;
    }) => {
      const f = input.fields;
      const lines = f.address
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const { error } = await supabase.rpc("set_campus", {
        p_tenant_id: tenantId ?? "",
        p_name: f.name.trim(),
        ...(input.campusId === null ? {} : { p_campus_id: input.campusId }),
        ...(f.slug.trim().length === 0 ? {} : { p_slug: f.slug.trim() }),
        p_address_lines: lines,
        ...(f.phone.trim().length === 0 ? {} : { p_phone: f.phone.trim() }),
        ...(f.mapUrl.trim().length === 0 ? {} : { p_map_url: f.mapUrl.trim() }),
        ...(f.timeZone.trim().length === 0 ? {} : { p_time_zone: f.timeZone.trim() }),
        p_service_times: f.serviceTimes as unknown as Json,
        p_sort_order: f.sortOrder,
        p_description: f.description.trim(),
        p_is_listed: f.isListed,
      });
      if (error) throw Object.assign(new Error(error.message), { code: error.code });
    },
    onSuccess: invalidate,
  });
}

export function useSetCampusActive() {
  const supabase = useSupabase();
  const invalidate = useCampusInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly campusId: string; readonly isActive: boolean }) => {
      const { error } = await supabase.rpc("set_campus_active", {
        p_campus_id: input.campusId,
        p_is_active: input.isActive,
      });
      if (error) throw Object.assign(new Error(error.message), { code: error.code });
    },
    onSuccess: invalidate,
  });
}
